import { dateToTimestamp, formatPrice, formatPriceCompact } from '@/utils'
import { resolveProductModelNo, resolveProductName } from '~/utils/tracking/helpers'
import {
  calcBuyGiftSingleTierSaveAmount,
  formatBuyGiftThresholdSuffix,
  formatPromotionSaveAmountText,
  getHighestBuyGiftTier,
  mapBuyGiftTierPriceTags,
  resolveBuyGiftPromoTagTexts,
  resolveBuyGiftTierBuyNum,
  resolveBuyGiftTierGiftTotalValue,
  sortBuyGiftTiers,
} from './buyGiftPromotion'
import { PROMOTION_TYPE, isComboPackage } from './promotionTypes'

/**
 * @typedef {Object} PromotionTabDef
 * @property {string} value - 二级页 Tab 值
 * @property {number} activityType - 活动类型 1~5
 * @property {string} label - 二级页展示名
 * @property {string} homeTitle - 首页模块标题
 * @property {string} moreTo - 更多跳转
 */

/** @type {PromotionTabDef[]} */
export const PROMOTION_TAB_DEFS = [
  {
    value: 'sale',
    activityType: PROMOTION_TYPE.VOLUME_PRICE,
    label: '限时特价',
    homeTitle: '特价优惠',
    moreTo: '/store/activity?tab=sale',
  },
  {
    value: 'discount',
    activityType: PROMOTION_TYPE.VOLUME_DISCOUNT,
    label: '限时折扣',
    homeTitle: '限时折扣',
    moreTo: '/store/activity?tab=discount',
  },
  {
    value: 'gift',
    activityType: PROMOTION_TYPE.BUY_GIFT,
    label: '买赠专区',
    homeTitle: '买赠专区',
    moreTo: '/store/activity?tab=gift',
  },
  {
    value: 'bundle',
    activityType: PROMOTION_TYPE.COMBO_BUNDLE,
    label: '搭配优惠',
    homeTitle: '搭配优惠',
    moreTo: '/store/activity?tab=bundle',
  },
  {
    value: 'combo',
    activityType: PROMOTION_TYPE.COMBO_PROMO,
    label: '组合促销',
    homeTitle: '组合促销',
    moreTo: '/store/activity?tab=combo',
  },
]

/** @type {Record<number, string>} */
export const TAB_VALUE_BY_ACTIVITY_TYPE = PROMOTION_TAB_DEFS.reduce((acc, item) => {
  acc[item.activityType] = item.value
  return acc
}, {})

/** @type {Record<string, number>} */
export const ACTIVITY_TYPE_BY_TAB_VALUE = PROMOTION_TAB_DEFS.reduce((acc, item) => {
  acc[item.value] = item.activityType
  return acc
}, {})

/**
 * @description 解析活动统计接口数据
 * @param {unknown} res
 * @returns {Array<Object>}
 */
export function unwrapPromotionStatistic(res) {
  if (Array.isArray(res)) return res
  if (Array.isArray(res?.data)) return res.data
  return []
}

/**
 * @description 解析活动列表接口数据
 * @param {unknown} res
 * @returns {{ items: Array<Object>, total: number }}
 */
export function unwrapPromotionList(res) {
  if (Array.isArray(res?.items)) {
    return { items: res.items, total: Number(res.total) || res.items.length }
  }
  if (Array.isArray(res?.data?.items)) {
    return { items: res.data.items, total: Number(res.data.total) || res.data.items.length }
  }
  if (Array.isArray(res)) {
    return { items: res, total: res.length }
  }
  return { items: [], total: 0 }
}

/**
 * @description 活动卡片展示价（去掉千分位，保留必要小数）
 * @param {number|string} value
 * @returns {string}
 */
export function formatPromotionCardPrice(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return ''
  return formatPriceCompact(num)
}

/**
 * @description 折扣比例转「X折」数字部分
 * @param {number|string} discount
 * @returns {string}
 */
export function formatPromotionDiscountFold(discount) {
  const num = Number(discount)
  if (!Number.isFinite(num) || num <= 0 || num > 1) return ''
  const fold = Math.round(num * 100) / 10
  return String(fold).replace(/\.0$/, '')
}

/**
 * @description 计算节省金额
 * @param {number|string} displayPrice
 * @param {number|string} originalPrice
 * @returns {number}
 */
export function calcPromotionSaveAmount(displayPrice, originalPrice) {
  const display = Number(displayPrice)
  const original = Number(originalPrice)
  if (!Number.isFinite(display) || !Number.isFinite(original) || original <= display) return 0
  return original - display
}

/**
 * @description 活动商品价目表单价（身份价目表价，无则终端客户价；不含活动价）
 * @param {Object|null|undefined} source
 * @returns {number|null}
 */
export function pickPromotionCatalogUnitPrice(source) {
  if (!source || typeof source !== 'object') return null
  const keys = [
    'originalPrice',
    'originalUnitPrice',
    'unitPrice',
    'listPrice',
    'catalogPrice',
    'oriPrice',
    'smbPrice',
    'terminalPrice',
  ]
  for (const key of keys) {
    const value = Number(source[key])
    if (Number.isFinite(value) && value > 0) return value
  }
  return null
}

/**
 * @description 活动列表主商品价目表价（优先 price.originalPrice，回退 SKU / 商品字段）
 * @param {Object|null|undefined} product
 * @returns {number|null}
 */
export function resolvePromotionProductCatalogPrice(product) {
  if (!product || typeof product !== 'object') return null

  const priceInfo = product.price && typeof product.price === 'object' ? product.price : {}
  const fromPriceInfo = pickPromotionCatalogUnitPrice(priceInfo)
  if (fromPriceInfo !== null) return fromPriceInfo

  const fromProduct = pickPromotionCatalogUnitPrice(product)
  if (fromProduct !== null) return fromProduct

  const skus = Array.isArray(product.skus) ? product.skus : []
  const skuPrices = skus
    .map((sku) => pickPromotionCatalogUnitPrice(sku))
    .filter((value) => value !== null && value > 0)
  if (skuPrices.length) return Math.min(...skuPrices)

  return null
}

/**
 * @description 活动阶梯价目表单价（不含 promotionPrice / displayPrice）
 * @param {Object|null|undefined} tier
 * @param {Object|null|undefined} [product]
 * @returns {number|null}
 */
export function resolvePromotionTierCatalogUnitPrice(tier, product) {
  const fromTier = pickPromotionCatalogUnitPrice(tier)
  if (fromTier !== null) return fromTier
  return resolvePromotionProductCatalogPrice(product)
}

/**
 * @description 取最小阶梯
 * @param {Array<Object>} tiers
 * @returns {Object|null}
 */
export function getLowestPromotionTier(tiers = []) {
  if (!Array.isArray(tiers) || tiers.length === 0) return null
  return [...tiers].sort((a, b) => Number(a.buyNum) - Number(b.buyNum))[0]
}

/**
 * @description 活动列表商品默认 SKU：取 `products[].skus[0].skuId`
 * @param {Object|null|undefined} product
 * @returns {number|null}
 */
export function resolvePromotionListDefaultSkuId(product) {
  const skus = Array.isArray(product?.skus) ? product.skus : []
  const first = skus[0]
  if (!first || typeof first !== 'object') return null
  const raw = first.skuId ?? first.id ?? first.prodSkuId
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * @description 按活动类型取列表项上的活动 VO（combo / combinationPromotion / buyGift 等）
 * @param {Object} activity
 * @returns {Object|null}
 */
function resolveActivityPromotionVo(activity) {
  const activityType = Number(activity?.activityType)

  if (activityType === PROMOTION_TYPE.COMBO_BUNDLE) {
    return activity?.combo && typeof activity.combo === 'object' ? activity.combo : null
  }

  if (activityType === PROMOTION_TYPE.COMBO_PROMO) {
    return activity?.combinationPromotion && typeof activity.combinationPromotion === 'object'
      ? activity.combinationPromotion
      : null
  }

  if (activityType === PROMOTION_TYPE.BUY_GIFT) {
    const candidates = [activity?.buyGift, activity?.giveaway, activity?.buyGiftPromotion]
    return candidates.find((item) => item && typeof item === 'object') || null
  }

  return null
}

/**
 * @description 活动 VO 上的节省金额（立省）：优先 minDiscountAmount，兼容 maxDiscountAmount / discountAmount；负数与 0 统一按 0 展示
 * @param {Object} activity
 * @returns {number|null}
 */
export function resolveActivityMinDiscountAmount(activity) {
  const vo = resolveActivityPromotionVo(activity)
  if (!vo) return null

  const raw = vo.minDiscountAmount ?? vo.maxDiscountAmount ?? vo.discountAmount
  const amount = Number(raw)
  if (!Number.isFinite(amount)) return null
  return Math.max(0, amount)
}

/**
 * @description 基于 minDiscountAmount 构建「立省 ¥X」卡片价
 * @param {Object} activity
 * @returns {{ pricePrefix: string, price: string, priceSuffix: string, currencySymbol: string }|null}
 */
function buildActivityMinDiscountPriceDisplay(activity) {
  const amount = resolveActivityMinDiscountAmount(activity)
  if (amount === null) return null

  const vo = resolveActivityPromotionVo(activity) || {}
  const tiers = Array.isArray(vo.tiers) ? vo.tiers : []
  const hasLadder = tiers.length > 1

  return {
    pricePrefix: '立省',
    price: formatPromotionCardPrice(amount),
    priceSuffix: hasLadder ? '起' : '',
    currencySymbol: '¥',
  }
}

/**
 * @description 买赠活动卡片价：无阶梯取赠品售价展示；多阶梯取最高档赠品售价之和（最高省 ¥N）
 * @param {Array<Object>} tiers
 * @param {string} [unit='台']
 * @param {{ discountCalc?: number }} [options]
 * @returns {{ cardDisplayPrice: string, cardPricePrefix: string, price: string, pricePrefix: string, priceSuffix: string, currencySymbol: string }|null}
 */
export function resolveBuyGiftPromotionPriceDisplay(tiers = [], unit = '台', options = {}) {
  if (!Array.isArray(tiers) || tiers.length === 0) return null

  const hasLadder = tiers.length > 1
  const tier = hasLadder ? getHighestBuyGiftTier(tiers) : sortBuyGiftTiers(tiers)[0]
  const giftTotalValue = resolveBuyGiftTierGiftTotalValue(tier)

  if (giftTotalValue === null || !Number.isFinite(giftTotalValue) || giftTotalValue < 0) return null

  if (hasLadder) {
    const price = formatPromotionSaveAmountText(giftTotalValue)
    return {
      cardDisplayPrice: formatPrice(giftTotalValue),
      cardPricePrefix: '最高省',
      price,
      pricePrefix: '最高省',
      priceSuffix: '',
      currencySymbol: '¥',
    }
  }

  const buyNum = resolveBuyGiftTierBuyNum(tier)
  if (!buyNum) return null

  const discountCalc = Number(options.discountCalc ?? tier?.discountCalc)
  const isLoop = discountCalc === 2
  const saveAmount = isLoop
    ? giftTotalValue
    : calcBuyGiftSingleTierSaveAmount(giftTotalValue, buyNum)
  if (saveAmount === null) return null

  const price = formatPromotionSaveAmountText(saveAmount)
  if (!price) return null

  return {
    cardDisplayPrice: formatPrice(saveAmount),
    cardPricePrefix: '立省',
    price,
    pricePrefix: '立省',
    priceSuffix: formatBuyGiftThresholdSuffix(buyNum, unit),
    currencySymbol: '¥',
  }
}

const ONE_DAY_MS = 24 * 3600 * 1000

/**
 * @description 活动倒计时钟面是否应展示（距目标不足 1 天）
 * @param {number} diffMs
 * @returns {boolean}
 */
export function isActivityCountdownWithinOneDay(diffMs) {
  const ms = Math.max(0, Math.floor(Number(diffMs) || 0))
  return ms > 0 && ms < ONE_DAY_MS
}

/**
 * @description 计算两个时间戳之间的日历天数差（按本地 0 点对齐）
 * @param {number} fromMs
 * @param {number} toMs
 * @returns {number}
 */
export function getActivityCalendarDayDiff(fromMs, toMs) {
  const from = new Date(fromMs)
  const to = new Date(toMs)
  const fromDay = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime()
  const toDay = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime()
  return Math.round((toDay - fromDay) / ONE_DAY_MS)
}

/**
 * @description 活动二级页快捷加购类型：特价 / 折扣 / 买赠
 * @param {number} activityType
 * @returns {boolean}
 */
export function isPromotionListQuickAddType(activityType) {
  const type = Number(activityType)
  return (
    type === PROMOTION_TYPE.VOLUME_PRICE ||
    type === PROMOTION_TYPE.VOLUME_DISCOUNT ||
    type === PROMOTION_TYPE.BUY_GIFT
  )
}

/**
 * @description 活动是否未开始（优先按起止时间判断）
 * @param {Object} activity
 * @param {number} [nowMs=Date.now()]
 * @returns {boolean}
 */
export function isPromotionActivityNotStarted(activity, nowMs = Date.now()) {
  const now = Number(nowMs) || Date.now()
  const startMs = dateToTimestamp(activity?.startTime)
  const endMs = dateToTimestamp(activity?.endTime)

  if (Number.isFinite(startMs) && Number.isFinite(endMs)) {
    if (now >= endMs) return false
    if (now >= startMs) return false
    return true
  }

  const status = activity?.activityStatus || activity?.displayStatus || ''
  return status === 'NOT_STARTED'
}

/**
 * @description 活动倒计时钟面：HH:mm:ss + 毫秒第一位（0.1 秒精度）
 * @param {number} diffMs
 * @returns {string}
 */
export function formatActivityCountdownClock(diffMs) {
  const ms = Math.max(0, Math.floor(Number(diffMs) || 0))
  const totalSec = Math.floor(ms / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  const msDigit = Math.floor((ms % 1000) / 100)
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${msDigit}`
}

/**
 * @description 活动倒计时拆分字段（供落地页/抽屉分块展示）
 * @param {number} diffMs
 * @returns {{ hours: string, minutes: string, seconds: string, msDigit: string }}
 */
export function resolveActivityCountdownClockParts(diffMs) {
  const ms = Math.max(0, Math.floor(Number(diffMs) || 0))
  const totalSec = Math.floor(ms / 1000)
  return {
    hours: String(Math.floor(totalSec / 3600)).padStart(2, '0'),
    minutes: String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0'),
    seconds: String(totalSec % 60).padStart(2, '0'),
    msDigit: String(Math.floor((ms % 1000) / 100)),
  }
}

/**
 * @description 距结束不足 1 天时的倒计时文案：仅剩 hh:mm:ss.d
 * @param {number} diffMs
 * @returns {string}
 */
export function formatActivityEndingCountdownText(diffMs) {
  return `仅剩 ${formatActivityCountdownClock(diffMs)}`
}

/**
 * @description 活动开始时刻 HH:mm（本地时区）
 * @param {number} startMs
 * @returns {string}
 */
export function formatActivityStartGrabClock(startMs) {
  const startDate = new Date(startMs)
  const hh = String(startDate.getHours()).padStart(2, '0')
  const mm = String(startDate.getMinutes()).padStart(2, '0')
  return `${hh}:${mm}`
}

/**
 * @description 活动卡片顶部倒计时色调：未开始（今天/明天/后天开抢）为 warning 橙，进行中快结束为主色
 * @param {{ countdownText?: string, countdownType?: string }} [liveCountdown]
 * @param {'warning'|'danger'} [fallbackTone='danger']
 * @returns {'warning'|'danger'}
 */
export function resolveCampaignTopTone(liveCountdown = {}, fallbackTone = 'danger') {
  const type = String(liveCountdown?.countdownType || '').trim()
  const text = String(liveCountdown?.countdownText || '').trim()

  if (type === 'upcoming') return 'warning'
  if (text.includes('开抢')) return 'warning'
  if (type === 'countdown') return 'danger'

  return fallbackTone === 'warning' ? 'warning' : 'danger'
}

/**
 * @description 活动卡片倒计时样式变体（upcoming=橙，countdown=主色红）
 * @param {{ countdownText?: string, countdownType?: string }} [liveCountdown]
 * @returns {'upcoming'|'countdown'|''}
 */
export function resolveCampaignCountdownVisualType(liveCountdown = {}) {
  const text = String(liveCountdown?.countdownText || '').trim()
  if (!text) return ''
  return resolveCampaignTopTone(liveCountdown) === 'warning' ? 'upcoming' : 'countdown'
}

/**
 * @description 活动卡片顶部倒计时文案（兼容静态映射字段）
 * @param {string} text
 * @param {string} [countdownType]
 * @returns {string}
 */
export function formatCampaignCountdownTopText(text, countdownType = '') {
  const trimmed = String(text || '').trim()
  if (!trimmed) return ''
  if (countdownType !== 'countdown') return trimmed
  const normalized = trimmed.replace(/^仅剩：/, '仅剩 ')
  if (/^仅剩\s/.test(normalized)) return normalized
  return `仅剩 ${trimmed}`
}

/**
 * @description 活动卡片顶部倒计时展示文案（实时优先；有起止时间时归零后不回退静态映射值）
 * @param {Object} [options]
 * @param {{ countdownText?: string, countdownType?: string }} [options.liveCountdown]
 * @param {string} [options.staticText]
 * @param {string} [options.countdownType]
 * @param {string|number} [options.startTime]
 * @param {string|number} [options.endTime]
 * @param {string} [options.directText]
 * @returns {string}
 */
export function resolveCampaignTopDisplayText({
  liveCountdown,
  staticText = '',
  countdownType = '',
  startTime,
  endTime,
  directText = '',
} = {}) {
  const liveText = String(liveCountdown?.countdownText || '').trim()
  if (liveText) return liveText

  if (startTime || endTime) return ''

  const direct = String(directText || '').trim()
  if (direct) return direct

  const type = countdownType || liveCountdown?.countdownType || ''
  return formatCampaignCountdownTopText(staticText, type)
}

/**
 * @description 活动商品倒计时/开抢文案
 * @reason 进行中且距结束<=1天 → 仅剩 hh:mm:ss.d；未开始按日历天展示今天/明天/后天开抢
 * @param {Object} activity
 * @param {number} [nowMs=Date.now()]
 * @returns {{ countdownText: string, countdownType: string }}
 */
export function resolveActivityCountdown(activity, nowMs = Date.now()) {
  const now = Number(nowMs) || Date.now()
  const startMs = dateToTimestamp(activity?.startTime)
  const endMs = dateToTimestamp(activity?.endTime)
  const explicitStatus = activity?.activityStatus || activity?.displayStatus || ''

  let status = explicitStatus
  if (!status && Number.isFinite(startMs) && Number.isFinite(endMs)) {
    if (now >= endMs) status = 'ENDED'
    else if (now >= startMs) status = 'IN_PROGRESS'
    else status = 'NOT_STARTED'
  } else if (Number.isFinite(startMs) && Number.isFinite(endMs)) {
    if (status !== 'NOT_STARTED') {
      if (now >= endMs) status = 'ENDED'
      else if (now >= startMs) status = 'IN_PROGRESS'
      else status = 'NOT_STARTED'
    }
  }

  if (status === 'NOT_STARTED') {
    if (!Number.isFinite(startMs) || startMs <= now) {
      return { countdownText: '', countdownType: '' }
    }

    const dayDiff = getActivityCalendarDayDiff(now, startMs)
    const startClock = formatActivityStartGrabClock(startMs)

    if (dayDiff === 0) {
      return {
        countdownText: `${startClock} 开抢`,
        countdownType: 'upcoming',
      }
    }

    if (dayDiff === 1) {
      return {
        countdownText: `明天 ${startClock} 开抢`,
        countdownType: 'upcoming',
      }
    }

    if (dayDiff === 2) {
      return {
        countdownText: `后天 ${startClock} 开抢`,
        countdownType: 'upcoming',
      }
    }

    return { countdownText: '', countdownType: '' }
  }

  if (Number.isFinite(endMs) && now >= endMs) {
    return { countdownText: '', countdownType: '' }
  }

  if (status === 'IN_PROGRESS' || (Number.isFinite(startMs) && startMs <= now)) {
    if (!Number.isFinite(endMs) || endMs <= now) {
      return { countdownText: '', countdownType: '' }
    }

    const diffMs = endMs - now
    if (diffMs > 0 && diffMs <= ONE_DAY_MS) {
      return {
        countdownText: formatActivityEndingCountdownText(diffMs),
        countdownType: 'countdown',
      }
    }

    return { countdownText: '', countdownType: '' }
  }

  return { countdownText: '', countdownType: '' }
}
/**
 * @description 根据统计接口构建二级页 Tab
 * @param {Array<Object>} stats
 * @returns {Array<{ label: string, value: string, activityType: number, sortOrder: number }>}
 */
export function buildPromotionTabsFromStatistic(stats = []) {
  const statMap = new Map(stats.map((item) => [Number(item.activityType), item]))

  return PROMOTION_TAB_DEFS.map((def) => {
    const stat = statMap.get(def.activityType)
    return {
      label: stat?.activityTypeText || def.label,
      value: def.value,
      activityType: def.activityType,
      sortOrder: Number(stat?.sortOrder) || def.activityType,
      totalCount: Number(stat?.totalCount) || 0,
    }
  })
    .filter((tab) => tab.totalCount > 0)
    .sort((a, b) => a.sortOrder - b.sortOrder)
}

/**
 * @description 活动列表拍平为商品行（二级页列表）
 * @param {Array<Object>} activities
 * @param {number} activityType
 * @returns {Array<Object>}
 */
export function flattenPromotionActivitiesToProducts(activities, activityType) {
  const rows = []

  activities.forEach((activity) => {
    const type = Number(activity?.activityType) || activityType

    if (isComboPackage(type)) {
      rows.push({
        activity,
        product: null,
        activityType: type,
      })
      return
    }

    const products = Array.isArray(activity?.products) ? activity.products : []

    if (products.length > 0) {
      products.forEach((product) => {
        rows.push({
          activity,
          product,
          activityType: type,
        })
      })
      return
    }

    rows.push({
      activity,
      product: null,
      activityType: type,
    })
  })

  return rows
}

/**
 * @description 活动卡片跳转（套餐/组合优先带 promotionId）
 * @param {Object} activity
 * @returns {string}
 */
function resolvePromotionActivityCardLink(activity) {
  const activityId = activity?.activityId ?? activity?.promotionId
  const activityType = Number(activity?.activityType)

  if (activityType === PROMOTION_TYPE.COMBO_BUNDLE) {
    const mainProdId = activity?.combo?.mainProduct?.prodId
    if (mainProdId) {
      const query = activityId ? `?promotionId=${encodeURIComponent(String(activityId))}` : ''
      return `/store/products/${mainProdId}${query}`
    }
  }

  if (activityType === PROMOTION_TYPE.COMBO_PROMO) {
    const products = Array.isArray(activity?.products) ? activity.products : []
    const prodId = products[0]?.prodId
    if (prodId) {
      const query = activityId ? `?promotionId=${encodeURIComponent(String(activityId))}` : ''
      return `/store/products/${prodId}${query}`
    }
  }

  const tabDef = PROMOTION_TAB_DEFS.find((item) => item.activityType === activityType)
  return tabDef?.moreTo || '#'
}

/**
 * @description 搭配套餐活动价展示（活动维度 combo）
 * @param {Object} activity
 * @returns {{ pricePrefix: string, price: string, priceSuffix: string, currencySymbol: string }}
 */
function resolveComboBundleActivityPriceDisplay(activity) {
  const minDiscountDisplay = buildActivityMinDiscountPriceDisplay(activity)
  if (minDiscountDisplay) {
    return {
      ...minDiscountDisplay,
      pricePrefix: '搭配立省',
      priceSuffix: minDiscountDisplay.priceSuffix || '起',
    }
  }

  const combo = activity?.combo || {}
  const comboPrice = Number(combo.comboTotalPrice)

  if (Number.isFinite(comboPrice) && comboPrice >= 0) {
    return {
      pricePrefix: '低至',
      price: formatPromotionCardPrice(comboPrice),
      priceSuffix: '起',
      currencySymbol: '¥',
    }
  }

  return {
    pricePrefix: '',
    price: '',
    priceSuffix: '',
    currencySymbol: '¥',
  }
}

/**
 * @description 组合促销活动价展示（活动维度 combinationPromotion）
 * @param {Object} activity
 * @returns {{ pricePrefix: string, price: string, priceSuffix: string, currencySymbol: string }}
 */
function resolveComboPromoActivityPriceDisplay(activity) {
  const minDiscountDisplay = buildActivityMinDiscountPriceDisplay(activity)
  if (minDiscountDisplay) return minDiscountDisplay

  const comboInfo = activity?.combinationPromotion || {}
  const tiers = Array.isArray(comboInfo.tiers) ? comboInfo.tiers : []
  const hasLadder = tiers.length > 1
  const discountRaw = Number(
    comboInfo.minDiscountAmount ?? comboInfo.maxDiscountAmount ?? comboInfo.discountAmount
  )

  if (Number.isFinite(discountRaw)) {
    return {
      pricePrefix: '立省',
      price: formatPromotionCardPrice(Math.max(0, discountRaw)),
      priceSuffix: hasLadder ? '起' : '',
      currencySymbol: '¥',
    }
  }

  const tier = getLowestPromotionTier(tiers)
  const tierAmount = Number(tier?.discountAmount ?? tier?.promotionPrice)
  if (Number.isFinite(tierAmount)) {
    return {
      pricePrefix: '立省',
      price: formatPromotionCardPrice(Math.max(0, tierAmount)),
      priceSuffix: hasLadder ? '起' : '',
      currencySymbol: '¥',
    }
  }

  return {
    pricePrefix: '',
    price: '',
    priceSuffix: '',
    currencySymbol: '¥',
  }
}

/**
 * @description 映射为搭配套餐二级页卡片结构（Figma 搭配套餐落地页）
 * @param {Object} activity
 * @returns {Object}
 */
export function mapPromotionActivityToBundleLandingCard(activity) {
  const card = mapPromotionActivityToActivityCard(activity)
  const hasSavePrice = card.pricePrefix === '立省' && String(card.price ?? '') !== ''

  return {
    ...card,
    pricePrefix: hasSavePrice ? '搭配立省' : card.pricePrefix,
    priceSuffix: hasSavePrice ? '起' : card.priceSuffix,
  }
}

/**
 * @description 映射为组合促销二级页卡片结构（Figma 营促销二级页-组合促销）
 * @param {Object} activity
 * @returns {Object}
 */
export function mapPromotionActivityToComboLandingCard(activity) {
  const card = mapPromotionActivityToActivityCard(activity)
  const hasSavePrice = card.pricePrefix === '立省' && String(card.price ?? '') !== ''

  return {
    ...card,
    promotionDescHtml: String(activity?.promotionDesc || '').trim(),
    priceSuffix: hasSavePrice ? '起' : card.priceSuffix,
    /** 保留原始活动数据，供抽屉等场景使用 */
    _rawActivity: activity,
  }
}

/**
 * @description 搭配套餐卡片文案：商品名称 + 套餐名称
 * @param {Object} activity
 * @returns {{ productName: string, packageName: string }}
 */
function resolveComboBundleCardText(activity) {
  const packageName = String(activity?.activityName || activity?.promotionName || '').trim()
  const mainProduct = activity?.combo?.mainProduct
  const productName = String(mainProduct?.prodName || mainProduct?.prodBrief || packageName).trim()

  return {
    productName,
    packageName,
  }
}

/**
 * @description 映射为 ActivityCard 结构（搭配套餐/组合促销，活动维度）
 * @param {Object} activity
 * @returns {Object}
 */
export function mapPromotionActivityToActivityCard(activity) {
  const activityType = Number(activity?.activityType)
  const countdown = resolveActivityCountdown(activity)
  const activityId = activity?.activityId ?? activity?.promotionId
  const priceDisplay =
    activityType === PROMOTION_TYPE.COMBO_BUNDLE
      ? resolveComboBundleActivityPriceDisplay(activity)
      : resolveComboPromoActivityPriceDisplay(activity)
  const bundleText =
    activityType === PROMOTION_TYPE.COMBO_BUNDLE ? resolveComboBundleCardText(activity) : null
  const activityName = activity?.activityName || activity?.promotionName || ''

  return {
    id: String(activityId || activity?.activityName || 'act'),
    name: bundleText?.productName || activityName,
    productName: bundleText?.productName || '',
    packageName: bundleText?.packageName || '',
    subtitle: activity?.promotionDesc || '',
    image: activity?.promotionPicture || '',
    to: resolvePromotionActivityCardLink(activity),
    cartAction: false,
    activityId,
    promotionId: activityId,
    activityName,
    promotionName: activityName,
    promotionType: activityType,
    prodId: null,
    activityStartTime: activity?.startTime,
    activityEndTime: activity?.endTime,
    activityStatus: activity?.activityStatus,
    tags: Array.isArray(activity?.labels) ? activity.labels : [],
    statusText: activity?.activityStatusText || '',
    pricePrefix: priceDisplay.pricePrefix,
    price: priceDisplay.price,
    priceSuffix: priceDisplay.priceSuffix,
    saveText: '',
    discountText: '',
    currencySymbol: priceDisplay.currencySymbol,
    ...countdown,
  }
}

/**
 * @description 映射为 ActivityCard 商品结构
 * @param {Object} product
 * @param {Object} activity
 * @returns {Object}
 */
export function mapPromotionProductToActivityCard(product, activity) {
  const activityType = Number(activity?.activityType)

  if (isComboPackage(activityType)) {
    return mapPromotionActivityToActivityCard(activity)
  }
  const unit = product?.prodUnit || '台'
  const priceInfo = product?.price || {}
  const displayPrice = priceInfo.displayPrice
  const originalPrice = priceInfo.originalPrice
  const tiers = product?.tiers || []
  const lowestTier = getLowestPromotionTier(tiers)
  const saveAmount = calcPromotionSaveAmount(displayPrice, originalPrice)
  const countdown = resolveActivityCountdown(activity)

  const base = {
    id: `${activity?.activityId || 'act'}-${product?.prodId || 'item'}`,
    name: resolveProductName(product) || product?.prodName || activity?.activityName || '',
    subtitle: resolveProductModelNo(product) || product?.prodModel || '',
    productModel: resolveProductModelNo(product) || product?.prodModel || '',
    modelNo: resolveProductModelNo(product) || product?.prodModel || '',
    prodName: resolveProductName(product) || product?.prodName || '',
    image: product?.prodImage || product?.pic || activity?.promotionPicture || '',
    to: product?.prodId ? `/store/products/${product.prodId}` : '#',
    cartAction: Boolean(product?.prodId),
    activityId: activity?.activityId ?? activity?.promotionId,
    promotionId: activity?.activityId ?? activity?.promotionId,
    activityName: activity?.activityName || activity?.promotionName || '',
    promotionName: activity?.promotionName || activity?.activityName || '',
    promotionType: activityType,
    prodId: product?.prodId,
    activityStartTime: activity?.startTime,
    activityEndTime: activity?.endTime,
    activityStatus: activity?.activityStatus,
    ...countdown,
  }

  if (!product) {
    return {
      ...base,
      price: '',
      pricePrefix: '',
      priceSuffix: '',
      saveText: '',
      discountText: '',
      currencySymbol: '¥',
      statusText: activity?.activityStatusText || '',
      cartAction: false,
    }
  }

  if (activityType === PROMOTION_TYPE.VOLUME_PRICE) {
    const saveText = `省¥${Math.round(Math.max(0, saveAmount))}`
    return {
      ...base,
      pricePrefix: '最低至',
      price: formatPromotionCardPrice(displayPrice),
      priceSuffix: `/${unit}`,
      saveText,
      discountText: '',
      currencySymbol: '¥',
    }
  }

  if (activityType === PROMOTION_TYPE.VOLUME_DISCOUNT) {
    const fold = formatPromotionDiscountFold(lowestTier?.promotionDiscount)
    const saveText = `省¥${Math.round(Math.max(0, saveAmount))}`

    if (fold) {
      return {
        ...base,
        pricePrefix: '低至',
        price: fold,
        priceSuffix: '折',
        saveText,
        discountText: '',
        currencySymbol: '',
      }
    }

    return {
      ...base,
      pricePrefix: '最低至',
      price: formatPromotionCardPrice(displayPrice),
      priceSuffix: `/${unit}`,
      saveText,
      discountText: '',
      currencySymbol: '¥',
    }
  }

  if (activityType === PROMOTION_TYPE.BUY_GIFT) {
    const buyGiftVo = resolveActivityPromotionVo(activity)
    const buyGiftPrice = resolveBuyGiftPromotionPriceDisplay(tiers, unit, {
      discountCalc: buyGiftVo?.discountCalc ?? activity?.discountCalc,
    })

    return {
      ...base,
      pricePrefix: buyGiftPrice?.pricePrefix ?? '',
      price: buyGiftPrice?.price ?? '',
      priceSuffix: buyGiftPrice?.priceSuffix ?? '',
      saveText: '',
      discountText: '',
      currencySymbol: buyGiftPrice?.currencySymbol ?? '¥',
    }
  }

  return {
    ...base,
    pricePrefix: displayPrice ? '最低至' : '',
    price: formatPromotionCardPrice(displayPrice),
    priceSuffix: displayPrice ? `/${unit}` : '',
    saveText: '',
    discountText: '',
    currencySymbol: '¥',
  }
}

/**
 * @description 阶梯数量展示（去小数尾零）
 * @param {number|string} value
 * @returns {string}
 */
function formatPromotionTierQuantity(value) {
  const num = Number(value)
  if (!Number.isFinite(num) || num <= 0) return ''
  if (Number.isInteger(num)) return String(num)
  return String(Number(num.toFixed(2))).replace(/\.?0+$/, '')
}

/**
 * @description 阶梯单价展示（千分位，去掉无意义 .00）
 * @param {number|string} value
 * @returns {string}
 */
function formatPromotionTierUnitPrice(value) {
  const num = Number(value)
  if (!Number.isFinite(num) || num <= 0) return ''
  const formatted = formatPrice(num)
  const dotIndex = formatted.lastIndexOf('.')
  if (dotIndex === -1) return formatted
  const integerPart = formatted.slice(0, dotIndex)
  const decimalPart = formatted.slice(dotIndex + 1).replace(/0+$/, '')
  return decimalPart ? `${integerPart}.${decimalPart}` : integerPart
}

/**
 * @description 阶梯折扣展示（X 折）
 * @param {number|string} value
 * @returns {string}
 */
function formatPromotionTierDiscountZhe(value) {
  let num = Number(value)
  if (!Number.isFinite(num) || num <= 0) return ''
  if (num > 0 && num <= 1) num *= 10
  if (num > 10 && num <= 100) num /= 10
  if (Number.isInteger(num)) return String(num)
  return String(Number(num.toFixed(1))).replace(/\.0$/, '')
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * @description 将接口 displayText 规范为活动商品卡标签格式
 * @param {string} text
 * @returns {string}
 */
export function normalizePromotionTierPriceTagLabel(text, unit = '台') {
  let normalized = String(text || '').trim()
  if (!normalized) return ''
  const safeUnit = String(unit || '台').trim() || '台'

  normalized = normalized
    .replace(/[台件]/g, safeUnit)
    .replace(/\/[台件]/g, `/${safeUnit}`)
    .replace(/¥\s+/g, '¥')

  const unitPattern = escapeRegExp(safeUnit)
  const enjoyPipe = normalized.match(
    new RegExp(`^满(\\d+(?:\\.\\d+)?)${unitPattern}享[¥￥]([\\d,.]+)\\/${unitPattern}$`)
  )
  if (enjoyPipe) {
    const qty = formatPromotionTierQuantity(enjoyPipe[1])
    const price = formatPromotionTierUnitPrice(String(enjoyPipe[2]).replace(/,/g, ''))
    if (qty && price) return `满${qty}${safeUnit} | ¥${price}/${safeUnit}`
  }

  const pipeMatch = normalized.match(
    new RegExp(`^满(\\d+(?:\\.\\d+)?)${unitPattern}\\s*\\|\\s*[¥￥]([\\d,.]+)\\/${unitPattern}$`)
  )
  if (pipeMatch) {
    const qty = formatPromotionTierQuantity(pipeMatch[1])
    const price = formatPromotionTierUnitPrice(String(pipeMatch[2]).replace(/,/g, ''))
    if (qty && price) return `满${qty}${safeUnit} | ¥${price}/${safeUnit}`
  }

  const buyEnjoyMatch = normalized.match(
    new RegExp(`^买(\\d+(?:\\.\\d+)?)${unitPattern}享[¥￥]([\\d,.]+)(?:\\/${unitPattern})?$`)
  )
  if (buyEnjoyMatch) {
    const qty = formatPromotionTierQuantity(buyEnjoyMatch[1])
    const price = formatPromotionTierUnitPrice(String(buyEnjoyMatch[2]).replace(/,/g, ''))
    if (qty && price) return `满${qty}${safeUnit} | ¥${price}/${safeUnit}`
  }

  return normalized
}

/**
 * @description 商详满量特价单阶梯标签：`满X台 | ¥Y/台`
 * @param {Object|null|undefined} tier
 * @returns {string}
 */
export function resolveVolumePriceDetailTierTagLabel(tier, unit = '台') {
  if (!tier || typeof tier !== 'object') return ''
  const safeUnit = String(unit || tier.prodUnit || '台').trim() || '台'

  const buyNum = formatPromotionTierQuantity(
    tier.buyNum ?? tier.needNum ?? tier.threshold ?? tier.count ?? tier.minNum
  )
  const displayText = tier.displayText ?? tier.label ?? tier.text

  if (!buyNum) {
    return displayText ? normalizePromotionTierPriceTagLabel(displayText, safeUnit) : ''
  }

  const promoPrice = Number(tier.promotionPrice ?? tier.promoPrice)
  const priceText =
    Number.isFinite(promoPrice) && promoPrice > 0 ? formatPromotionTierUnitPrice(promoPrice) : ''

  if (!priceText) {
    return displayText ? normalizePromotionTierPriceTagLabel(displayText, safeUnit) : ''
  }

  return `满${buyNum}${safeUnit} | ¥${priceText}/${safeUnit}`
}

/**
 * @description 商详满量特价阶梯标签列表
 * @param {Object|null|undefined} volumePriceActivity
 * @returns {string[]}
 */
export function buildVolumePriceDetailPromoTags(volumePriceActivity, options = {}) {
  if (!volumePriceActivity || typeof volumePriceActivity !== 'object') return []
  const safeUnit = String(options.unit || volumePriceActivity.prodUnit || '台').trim() || '台'

  const tiers = Array.isArray(volumePriceActivity.tiers) ? volumePriceActivity.tiers : []
  if (tiers.length) {
    const sorted = [...tiers].sort((a, b) => Number(a.buyNum) - Number(b.buyNum))
    const tags = sorted
      .map((tier) => resolveVolumePriceDetailTierTagLabel(tier, safeUnit))
      .filter(Boolean)
    if (tags.length) return tags
  }

  const labels = Array.isArray(volumePriceActivity.labels) ? volumePriceActivity.labels : []
  return labels
    .map((item) => normalizePromotionTierPriceTagLabel(item?.displayText, safeUnit))
    .filter(Boolean)
}

/**
 * @description 根据阶梯数据生成活动商品卡满减/特价标签
 * @param {Object} tier
 * @param {number} activityType
 * @returns {string}
 */
export function resolvePromotionTierPriceTagLabel(tier, activityType, unit = '台') {
  if (!tier || typeof tier !== 'object') return ''
  const safeUnit = String(unit || tier.prodUnit || '台').trim() || '台'

  const buyNum = formatPromotionTierQuantity(
    tier.buyNum ?? tier.needNum ?? tier.threshold ?? tier.count ?? tier.minNum
  )

  if (activityType === PROMOTION_TYPE.VOLUME_PRICE && buyNum) {
    const catalogPrice = resolvePromotionTierCatalogUnitPrice(tier)
    const price = formatPromotionTierUnitPrice(catalogPrice)
    if (price) return `满${buyNum}${safeUnit} | ¥${price}/${safeUnit}`
  }

  if (activityType === PROMOTION_TYPE.VOLUME_DISCOUNT && buyNum) {
    const zhe = formatPromotionTierDiscountZhe(
      tier.promotionDiscount ?? tier.discount ?? tier.discountValue
    )
    if (zhe) return `满${buyNum}${safeUnit}享${zhe}折`
  }

  const displayText = tier.displayText ?? tier.label ?? tier.text
  return displayText ? normalizePromotionTierPriceTagLabel(displayText, safeUnit) : ''
}

/**
 * @description 映射为 ProductListCard 商品结构（活动二级页）
 * @param {Object} product
 * @param {Object} activity
 * @returns {Object}
 */
export function mapPromotionProductToListCard(product, activity) {
  const activityType = Number(activity?.activityType)
  const tiers = Array.isArray(product?.tiers) ? product.tiers : []
  const unit = String(product?.prodUnit || '台').trim() || '台'
  const countdown = resolveActivityCountdown(activity)
  const catalogPrice = resolvePromotionProductCatalogPrice(product)
  const buyGiftVo =
    activityType === PROMOTION_TYPE.BUY_GIFT ? resolveActivityPromotionVo(activity) : null

  const resolveCardDisplayPrice = () => {
    if (catalogPrice !== null) {
      return formatPromotionSaveAmountText(catalogPrice)
    }
    return ''
  }

  const isQuickAddType = isPromotionListQuickAddType(activityType)
  const defaultSkuId = isQuickAddType ? resolvePromotionListDefaultSkuId(product) : null
  const skus = Array.isArray(product?.skus) ? product.skus : []
  const notStarted = isQuickAddType && isPromotionActivityNotStarted(activity)

  return {
    prodId: product?.prodId,
    prodName: resolveProductName(product) || product?.prodName || activity?.activityName || '',
    modelNo: resolveProductModelNo(product) || product?.prodModel || '',
    brief:
      resolveBuyGiftPromoTagTexts(product).join(' ') ||
      tiers[0]?.displayText ||
      activity?.promotionDesc ||
      activity?.activityName ||
      '',
    pic: product?.prodImage || product?.pic || activity?.promotionPicture || '',
    ...(catalogPrice !== null ? { price: catalogPrice } : {}),
    cardDisplayPrice: resolveCardDisplayPrice(),
    originalPrice: '',
    ...(defaultSkuId != null ? { skuId: defaultSkuId, defaultSkuId } : {}),
    ...(skus.length ? { skus, skuList: skus } : {}),
    tierPriceTags:
      activityType === PROMOTION_TYPE.BUY_GIFT
        ? mapBuyGiftTierPriceTags({
            ...product,
            discountCalc: buyGiftVo?.discountCalc ?? activity?.discountCalc,
          })
        : tiers
            .map((tier) => {
              const label = resolvePromotionTierPriceTagLabel(tier, activityType, unit)
              if (!label) return null
              return {
                label,
                threshold: tier.buyNum,
                price: resolvePromotionTierCatalogUnitPrice(tier, product),
              }
            })
            .filter(Boolean),
    countdownText: countdown.countdownText,
    campaignCountdownType: countdown.countdownType,
    campaignTopTone: resolveCampaignTopTone(countdown),
    promotionType: activityType,
    promotionId: activity?.activityId ?? activity?.promotionId,
    activityId: activity?.activityId ?? activity?.promotionId,
    activityName: activity?.activityName || activity?.promotionName || '',
    promotionName: activity?.promotionName || activity?.activityName || '',
    prodUnit: product?.prodUnit || '',
    discountCalc: buyGiftVo?.discountCalc ?? activity?.discountCalc,
    storeDetailQuery: activity?.activityId ? { promotionId: activity.activityId } : undefined,
    activityStatus: activity?.activityStatus,
    activityStartTime: activity?.startTime,
    activityEndTime: activity?.endTime,
    activityStatusText: activity?.activityStatusText,
    cartAction: isQuickAddType ? !notStarted : true,
    campaignActionLabel: notStarted ? '未开始' : '',
  }
}

/**
 * @description 首页活动模块：按类型聚合活动商品
 * @param {Array<Object>} activities
 * @param {number} activityType
 * @param {number} [maxProducts=6]
 * @returns {Object|null}
 */
export function mapPromotionActivitiesToHomeSection(activities, activityType, maxProducts = 6) {
  const tabDef = PROMOTION_TAB_DEFS.find((item) => item.activityType === activityType)
  if (!tabDef) return null

  const cardMap = new Map()

  activities.forEach((activity) => {
    const type = Number(activity?.activityType) || activityType

    if (isComboPackage(type)) {
      const key = String(activity?.activityId ?? activity?.promotionId ?? activity?.activityName)
      if (!key || cardMap.has(key)) return
      cardMap.set(
        key,
        mapPromotionActivityToActivityCard({
          ...activity,
          activityType: type,
        })
      )
      return
    }

    const products = Array.isArray(activity?.products) ? activity.products : []
    products.forEach((product) => {
      const key = String(product?.prodId || product?.prodName)
      if (!key || cardMap.has(key)) return
      cardMap.set(
        key,
        mapPromotionProductToActivityCard(product, {
          ...activity,
          activityType: type,
        })
      )
    })
  })

  const products = Array.from(cardMap.values()).slice(0, maxProducts)
  if (products.length === 0) return null

  const firstActivity = activities[0] || {}

  return {
    id: `home-act-${activityType}`,
    title: firstActivity.activityTypeText || tabDef.homeTitle,
    moreText: '更多',
    moreTo: tabDef.moreTo,
    activityType,
    products,
  }
}
